/**
 * Desktop Schedule Page — T17 Calendar with week/month view
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Calendar, ChevronLeft, ChevronRight, Loader2, Clock } from 'lucide-react';
import config from 'config';

const API = config.API_URL;
const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const DAYS_FULL = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const MONTHS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

function getWeekDates(date) {
  const d = new Date(date);
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1);
  return Array.from({ length: 7 }, (_, i) => {
    const dd = new Date(d);
    dd.setDate(d.getDate() + i);
    return dd.toISOString().slice(0, 10);
  });
}

function getMonthDates(year, month) {
  const first = new Date(year, month, 1);
  const startDay = (first.getDay() || 7) - 1;
  const dates = [];
  for (let i = -startDay; i < 42 - startDay; i++) {
    const d = new Date(year, month, 1 + i);
    dates.push(d.toISOString().slice(0, 10));
  }
  return dates;
}

export default function DesktopSchedulePage() {
  const { token } = useOutletContext();
  const [view, setView] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  const dateRange = useMemo(() => {
    if (view === 'week') {
      const week = getWeekDates(currentDate);
      return { start: week[0], end: week[6], dates: week };
    }
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();
    const dates = getMonthDates(y, m);
    return { start: dates[0], end: dates[dates.length - 1], dates };
  }, [view, currentDate]);

  const fetchShifts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/employees/me/schedule?start_date=${dateRange.start}&end_date=${dateRange.end}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setShifts(data.shifts || []);
      }
    } catch {}
    setLoading(false);
  }, [token, dateRange.start, dateRange.end]);

  useEffect(() => { fetchShifts(); }, [fetchShifts]);

  const shiftMap = useMemo(() => {
    const map = {};
    shifts.forEach(s => { map[s.date] = [...(map[s.date] || []), s]; });
    return map;
  }, [shifts]);

  const navigate = (dir) => {
    const d = new Date(currentDate);
    if (view === 'week') d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    setCurrentDate(d);
  };

  const today = new Date().toISOString().slice(0, 10);
  const curMonth = currentDate.getMonth();

  return (
    <div className="p-8 max-w-6xl" data-testid="desktop-schedule-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-white">Mon planning</h1>
          <p className="text-white/40 text-sm mt-1">Votre planning de travail</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1 bg-white/5 rounded-xl p-1">
            <button onClick={() => setView('week')} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${view === 'week' ? 'bg-[#C9A24D]/15 text-[#C9A24D]' : 'text-white/40 hover:text-white'}`} data-testid="desktop-view-week">Semaine</button>
            <button onClick={() => setView('month')} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${view === 'month' ? 'bg-[#C9A24D]/15 text-[#C9A24D]' : 'text-white/40 hover:text-white'}`} data-testid="desktop-view-month">Mois</button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(-1)} className="p-2 rounded-lg text-white/30 hover:text-white hover:bg-white/5 transition" data-testid="desktop-nav-prev"><ChevronLeft className="w-5 h-5" /></button>
            <span className="text-white/60 text-sm font-medium min-w-[200px] text-center">
              {view === 'week'
                ? `${new Date(dateRange.start).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} — ${new Date(dateRange.end).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`
                : `${MONTHS[curMonth]} ${currentDate.getFullYear()}`
              }
            </span>
            <button onClick={() => navigate(1)} className="p-2 rounded-lg text-white/30 hover:text-white hover:bg-white/5 transition" data-testid="desktop-nav-next"><ChevronRight className="w-5 h-5" /></button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" /></div>
      ) : view === 'week' ? (
        <div className="grid grid-cols-7 gap-3" data-testid="desktop-week-view">
          {dateRange.dates.map((date, i) => {
            const dayShifts = shiftMap[date] || [];
            const isToday = date === today;
            return (
              <div key={date} className={`rounded-2xl border p-4 min-h-[180px] transition ${isToday ? 'bg-[#C9A24D]/5 border-[#C9A24D]/20' : 'bg-white/[0.02] border-white/5'}`}>
                <div className="mb-3">
                  <p className={`text-xs font-semibold ${isToday ? 'text-[#C9A24D]' : 'text-white/25'}`}>{DAYS_FULL[i]}</p>
                  <p className={`text-lg font-bold ${isToday ? 'text-white' : 'text-white/50'}`}>
                    {new Date(date).getDate()}
                  </p>
                </div>
                {dayShifts.length > 0 ? dayShifts.map(s => (
                  <div key={s.id} className="p-2.5 rounded-xl bg-blue-500/10 border border-blue-500/15 mb-2">
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-blue-400" />
                      <span className="text-blue-300 text-xs font-semibold">{s.shift_start} — {s.shift_end}</span>
                    </div>
                    {s.notes && <p className="text-white/25 text-[10px] mt-1 truncate">{s.notes}</p>}
                  </div>
                )) : (
                  <p className="text-white/10 text-xs">Repos</p>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div data-testid="desktop-month-view">
          <div className="grid grid-cols-7 gap-px mb-2">
            {DAYS.map(d => <div key={d} className="text-center text-white/25 text-xs font-medium py-2">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-2">
            {dateRange.dates.slice(0, 42).map(date => {
              const d = new Date(date);
              const inMonth = d.getMonth() === curMonth;
              const isToday = date === today;
              const dayShifts = shiftMap[date] || [];
              return (
                <div key={date} className={`min-h-[80px] p-2 rounded-xl border transition ${isToday ? 'border-[#C9A24D]/30 bg-[#C9A24D]/5' : inMonth ? 'border-white/5 bg-white/[0.02]' : 'border-transparent'}`}>
                  <span className={`text-xs font-medium ${isToday ? 'text-[#C9A24D]' : inMonth ? 'text-white/40' : 'text-white/10'}`}>{d.getDate()}</span>
                  {dayShifts.map(s => (
                    <div key={s.id} className="mt-1 px-2 py-1 rounded-lg bg-blue-500/15 text-blue-300 text-[10px] font-medium truncate">
                      {s.shift_start} - {s.shift_end}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {shifts.length === 0 && !loading && (
        <div className="text-center py-10 mt-4">
          <Calendar className="w-12 h-12 text-white/[0.06] mx-auto mb-3" />
          <p className="text-white/20 text-sm">Aucun shift prévu pour cette période</p>
        </div>
      )}
    </div>
  );
}
